import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '../../api/client.js';

/**
 * The Power BI content library for this customer's workspace.
 *
 * This is the step that was missing from the admin UI: `POST /admin/catalogue/sync`
 * existed on the API but nothing called it, so `portal.Reports` stayed empty
 * and there was nothing to pick when building a profile.
 *
 * Nothing here grants access — the catalogue is just "what exists in the
 * workspace". Entitlement is still per-profile, and the embed endpoints check
 * it independently.
 */
export default function AdminCatalogue() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'catalogue'], queryFn: admin.getCatalogue
  });
  const { data: diag } = useQuery({
    queryKey: ['admin', 'diagnostics'], queryFn: admin.getDiagnostics
  });

  const sync = useMutation({
    mutationFn: admin.syncCatalogue,
    onMutate: () => { setResult(null); setError(null); },
    onSuccess: (counts) => {
      setResult(counts);
      queryClient.invalidateQueries({ queryKey: ['admin', 'catalogue'] });
    },
    onError: (err) => setError(err.body?.error ?? err.message)
  });

  if (isLoading) return <div className="admin-page"><div className="spinner-border" /></div>;

  const noWorkspace = !data?.workspaceId;
  const isEmpty = !noWorkspace && data.reports.length === 0
    && data.dashboards.length === 0 && data.datasets.length === 0;

  return (
    <div className="admin-page">
      <div className="d-flex align-items-center mb-3">
        <h1 className="h4 mb-0">Power BI Content</h1>
        <button type="button" className="btn btn-primary btn-sm ms-auto"
                disabled={noWorkspace || sync.isPending}
                onClick={() => sync.mutate()}>
          <i className="bi bi-arrow-repeat" />{' '}
          {sync.isPending ? 'Syncing…' : 'Sync from Power BI'}
        </button>
      </div>

      {noWorkspace && (
        <div className="alert alert-warning">
          <div className="fw-semibold">No Power BI workspace is set for this customer.</div>
          <div className="small mt-1">
            Set <code>PowerBiWorkspaceId</code> on the row in <code>portal.Customers</code>,
            then sync. The workspace id is the GUID in the Power BI service URL:
            <code className="ms-1">app.powerbi.com/groups/&lt;workspace-id&gt;/…</code>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Why the Target dropdown might be empty even though profile data is
          saved correctly — the catalogue is filtered by the customer's
          workspace, so a mismatched workspace id yields an empty list. */}
      {diag && (
        <details className="mb-3" open={diag.profileTargets.unresolved > 0
                                        || diag.catalogueForThisWorkspace.Reports === 0}>
          <summary className="small fw-semibold" style={{ cursor: 'pointer' }}>
            Diagnostics
          </summary>
          <div className="mt-2 small">
            <div className={`alert py-2 ${
              diag.likelyCause.startsWith('No problem') ? 'alert-success' : 'alert-warning'}`}>
              {diag.likelyCause}
            </div>
            <table className="table table-sm mb-0">
              <tbody>
                <tr>
                  <td>Customer workspace</td>
                  <td><code className="small">{diag.customerWorkspaceId ?? 'not set'}</code></td>
                </tr>
                <tr>
                  <td>Catalogue for this workspace</td>
                  <td>
                    {diag.catalogueForThisWorkspace.Reports} reports,{' '}
                    {diag.catalogueForThisWorkspace.Dashboards} dashboards,{' '}
                    {diag.catalogueForThisWorkspace.Datasets} datasets
                  </td>
                </tr>
                <tr>
                  <td>Catalogue across all workspaces</td>
                  <td>
                    {diag.catalogueTotalAllWorkspaces.Reports} reports,{' '}
                    {diag.catalogueTotalAllWorkspaces.Dashboards} dashboards,{' '}
                    {diag.catalogueTotalAllWorkspaces.Datasets} datasets
                  </td>
                </tr>
                <tr>
                  <td>Workspaces present in catalogue</td>
                  <td>
                    {diag.workspacesPresentInCatalogue.length === 0
                      ? <span className="admin-muted">none</span>
                      : diag.workspacesPresentInCatalogue.map(w => (
                          <div key={w.workspaceId}>
                            <code className="small">{w.workspaceId}</code> — {w.reports} reports
                          </div>
                        ))}
                  </td>
                </tr>
                <tr>
                  <td>Saved profile targets</td>
                  <td>
                    {diag.profileTargets.checked} checked,{' '}
                    <strong>{diag.profileTargets.unresolved}</strong> not found in catalogue
                    {diag.profileTargets.examples.map(e => (
                      <div key={e.targetId} className="admin-muted">
                        <code className="small">{e.targetId}</code> {e.itemLabel ?? ''}
                      </div>
                    ))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      )}

      {result && (
        <div className="alert alert-success">
          Synced {result.reports} report(s), {result.dashboards} dashboard(s)
          and {result.datasets} dataset(s).{' '}
          {result.reports > 0 && (
            <>Now add them to a profile in{' '}
            <Link to="/admin/profiles">Profile Management</Link> so they appear in the menu.</>
          )}
        </div>
      )}

      {isEmpty && !result && (
        <div className="alert alert-info">
          <div className="fw-semibold">Nothing synced yet.</div>
          <div className="small mt-1">
            Press <strong>Sync from Power BI</strong> to pull the reports, dashboards
            and datasets from workspace <code>{data.workspaceId}</code>. If the sync
            returns nothing, check that the app registration&apos;s service principal
            has been added to that workspace as a member.
          </div>
        </div>
      )}

      {!noWorkspace && !isEmpty && (
        <>
          <p className="text-muted small">
            Workspace <code>{data.workspaceId}</code>
            {data.lastSyncedUtc && <> · last synced {new Date(data.lastSyncedUtc).toLocaleString()}</>}
            {' '}· Syncing does not grant anyone access; add items to a profile in{' '}
            <Link to="/admin/profiles">Profile Management</Link>.
          </p>

          <Section title="Reports" count={data.reports.length}>
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Type</th><th>Dataset</th><th>Id</th></tr></thead>
              <tbody>
                {data.reports.map(r => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>
                      {r.reportType === 'PaginatedReport'
                        ? <span className="badge bg-info">Paginated</span>
                        : <span className="badge bg-secondary">Report</span>}
                    </td>
                    <td className="small text-muted">
                      {data.datasets.find(d => d.id === r.datasetId)?.name ?? '—'}
                    </td>
                    <td><code className="small">{r.id}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Dashboards" count={data.dashboards.length}>
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Id</th></tr></thead>
              <tbody>
                {data.dashboards.map(d => (
                  <tr key={d.id}><td>{d.name}</td><td><code className="small">{d.id}</code></td></tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Datasets" count={data.datasets.length}>
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Row-level security</th><th>Id</th></tr></thead>
              <tbody>
                {data.datasets.map(d => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>
                      {d.requiresRls
                        ? <span className="badge bg-warning text-dark">Identity required</span>
                        : <span className="text-muted small">none</span>}
                    </td>
                    <td><code className="small">{d.id}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  if (!count) return null;
  return (
    <div className="mb-4">
      <h2 className="h6">{title} <span className="badge bg-light text-dark">{count}</span></h2>
      {children}
    </div>
  );
}
